import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PostCard from "./components/PostCard";
import PostModal from "./components/PostModal";
import { mockPosts } from "@/mocks/community";

const categories = ["All", "Mindset", "Career", "Corporate", "Life", "Wellness", "Confidence"];
const sortOptions = ["Latest", "Top Rated", "Most Liked", "Most Commented"];

interface Post {
  id: string;
  author: string;
  avatar: string;
  niche: string;
  title: string;
  content: string;
  image: string;
  rating: number;
  ratingCount: number;
  comments: number;
  likes: number;
  tags: string[];
  timeAgo: string;
}

export default function CommunityPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeSort, setActiveSort] = useState("Latest");
  const [search, setSearch] = useState("");
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "" });

  const filtered = mockPosts.filter((p) => {
    const matchCat = activeCategory === "All" || p.tags.some((t) => t.toLowerCase() === activeCategory.toLowerCase());
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.author.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#F7F6F3]">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c24faad7145e3260647dc2f0fc977975.jpg"
            alt="Community"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Community Gallery
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Share. Inspire. Grow Together.
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto mb-8">
            Explore coaching insights, share your journey, and connect with a community of growth-minded individuals.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-8 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line mr-2" />
            Share Your Work
          </button>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts, coaches..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400 transition-colors"
              />
            </div>

            {/* Sort */}
            <select
              value={activeSort}
              onChange={(e) => setActiveSort(e.target.value)}
              className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-400 cursor-pointer"
            >
              {sortOptions.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 flex-wrap mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-teal-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-teal-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="flex items-center gap-6 mb-8 text-sm text-gray-500">
            <span><strong className="text-gray-900">{filtered.length}</strong> posts</span>
            <span><strong className="text-gray-900">200+</strong> coaches</span>
            <span><strong className="text-gray-900">12,000+</strong> members</span>
          </div>

          {/* Posts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <PostCard key={post.id} post={post} onOpen={setSelectedPost} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-line text-4xl text-gray-300 mb-3 block" />
              <p className="text-gray-400">No posts found. Try a different search or category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Post Modal */}
      {selectedPost && (
        <PostModal post={selectedPost} onClose={() => setSelectedPost(null)} />
      )}

      {/* Create Post Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-gray-900 text-lg">Share Your Work</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Post Title</label>
                <input
                  value={newPost.title}
                  onChange={(e) => setNewPost((p) => ({ ...p, title: e.target.value }))}
                  placeholder="What's your coaching insight?"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Content</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost((p) => ({ ...p, content: e.target.value }))}
                  placeholder="Share your coaching work, insights, or journey..."
                  rows={5}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{newPost.content.length}/500</p>
              </div>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-teal-300 transition-colors">
                <i className="ri-image-add-line text-2xl text-gray-300 mb-2 block" />
                <p className="text-sm text-gray-400">Click to upload an image</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Publish Post
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
